/**
 * Unified Patient Onboarding Flow
 * Consolidates all patient onboarding implementations into a single, robust system
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import unifiedPatientOnboardingService from '../../services/api/unifiedPatientOnboarding';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  HealthAndSafety as HealthIcon,
  Description as DocumentIcon,
  Assignment as IntakeIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { patientOnboardingService } from '../../services/api/patientOnboarding';
import { useSessionManager } from '../../hooks/useSessionManager';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  background: theme.palette.background.paper,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
}));

const UnifiedOnboardingFlow = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);
  const { getSessionStats } = useSessionManager();
  
  // State management
  const [onboardingState, setOnboardingState] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepData, setStepData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  // Check for intent parameter (from magic links)
  const intent = searchParams.get('intent');

  // Onboarding steps configuration
  const onboardingSteps = [
    {
      id: 'profile',
      title: 'Basic Profile',
      description: 'Complete your basic profile information',
      icon: <PersonIcon />,
      component: 'PatientProfileForm',
      required: true,
      estimatedTime: '2 minutes'
    },
    {
      id: 'biometrics',
      title: 'Health Information',
      description: 'Add your health and biometric information',
      icon: <HealthIcon />,
      component: 'PatientBiometricsForm',
      required: true,
      estimatedTime: '3 minutes'
    },
    {
      id: 'documents',
      title: 'Document Upload',
      description: 'Upload your medical documents and ID',
      icon: <DocumentIcon />,
      component: 'PatientDocumentUpload',
      required: false,
      estimatedTime: '5 minutes'
    },
    {
      id: 'intake',
      title: 'Intake Forms',
      description: 'Complete intake forms and questionnaires',
      icon: <IntakeIcon />,
      component: 'PatientIntakeForm',
      required: true,
      estimatedTime: '10 minutes'
    },
    {
      id: 'next-steps',
      title: 'Next Steps',
      description: 'Review your information and next steps',
      icon: <CheckIcon />,
      component: 'PatientNextSteps',
      required: true,
      estimatedTime: '2 minutes'
    }
  ];

  // Initialize onboarding
  useEffect(() => {
    initializeOnboarding();
  }, []);

  // Handle intent parameter
  useEffect(() => {
    if (intent) {
      handleIntent(intent);
    }
  }, [intent]);

  const initializeOnboarding = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const state = await unifiedPatientOnboardingService.getOnboardingState();
      setOnboardingState(state);
      
      // Determine current step based on onboarding state
      const stepIndex = determineCurrentStep(state);
      setCurrentStep(stepIndex);
      
      // Set completed steps
      const completed = new Set(state.completed_sections || []);
      setCompletedSteps(completed);
      
      // Load existing data for each step
      await loadStepData();
      
    } catch (err) {
      console.error('Error initializing onboarding:', err);
      setError('Failed to load onboarding information. Please try again.');
      toast.error('Failed to load onboarding information');
    } finally {
      setLoading(false);
    }
  };

  const determineCurrentStep = (state) => {
    const completedSections = state.completed_sections || [];
    
    for (let i = 0; i < onboardingSteps.length; i++) {
      const step = onboardingSteps[i];
      if (!completedSections.includes(step.id)) {
        return i;
      }
    }
    
    return onboardingSteps.length - 1; // All steps completed
  };

  const loadStepData = async () => {
    try {
      const data = {};
      
      // Load profile data
      if (onboardingState?.profile) {
        data.profile = onboardingState.profile;
      }
      
      // Load other step data
      for (const step of onboardingSteps) {
        if (step.id !== 'profile') {
          try {
            const stepData = await patientOnboardingService.getStepData(step.id);
            data[step.id] = stepData;
          } catch (err) {
            console.warn(`Failed to load data for step ${step.id}:`, err);
          }
        }
      }
      
      setStepData(data);
    } catch (err) {
      console.error('Error loading step data:', err);
    }
  };

  const handleIntent = (intent) => {
    switch (intent) {
      case 'profile':
        setCurrentStep(0);
        break;
      case 'biometrics':
        setCurrentStep(1);
        break;
      case 'documents':
        setCurrentStep(2);
        break;
      case 'intake':
        setCurrentStep(3);
        break;
      case 'next-steps':
        setCurrentStep(4);
        break;
      default:
        console.warn('Unknown intent:', intent);
    }
  };

  const handleStepComplete = async (stepId, data) => {
    try {
      setSaving(true);
      setError(null);
      
      // Validate step data
      const validationResult = await validateStepData(stepId, data);
      if (!validationResult.isValid) {
        setValidationErrors(validationResult.errors);
        return;
      }
      
      // Save step data
      await patientOnboardingService.saveStepData(stepId, data);
      
      // Mark step as complete
      await patientOnboardingService.completeSection(stepId);
      
      // Update local state
      setCompletedSteps(prev => new Set([...prev, stepId]));
      setStepData(prev => ({ ...prev, [stepId]: data }));
      setValidationErrors({});
      
      // Move to next step
      const nextStepIndex = currentStep + 1;
      if (nextStepIndex < onboardingSteps.length) {
        setCurrentStep(nextStepIndex);
      } else {
        // All steps completed
        handleOnboardingComplete();
      }
      
      toast.success(`${onboardingSteps[currentStep].title} completed successfully!`);
      
    } catch (err) {
      console.error('Error completing step:', err);
      setError(`Failed to save ${onboardingSteps[currentStep].title}. Please try again.`);
      toast.error(`Failed to save ${onboardingSteps[currentStep].title}`);
    } finally {
      setSaving(false);
    }
  };

  const validateStepData = async (stepId, data) => {
    try {
      const validation = await patientOnboardingService.validateStepData(stepId, data);
      return validation;
    } catch (err) {
      console.error('Validation error:', err);
      return { isValid: false, errors: { general: 'Validation failed' } };
    }
  };

  const handleOnboardingComplete = async () => {
    try {
      // Mark onboarding as complete
      await patientOnboardingService.completeOnboarding();
      
      // Redirect to patient dashboard
      navigate('/patient-dashboard');
      
      toast.success('Welcome to MindWell! Your onboarding is complete.');
      
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError('Failed to complete onboarding. Please try again.');
      toast.error('Failed to complete onboarding');
    }
  };

  const handleStepBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkipStep = async (stepId) => {
    try {
      setSaving(true);
      
      // Mark step as skipped
      await patientOnboardingService.skipSection(stepId);
      
      // Move to next step
      handleStepNext();
      
      toast.info('Step skipped. You can complete it later.');
      
    } catch (err) {
      console.error('Error skipping step:', err);
      setError('Failed to skip step. Please try again.');
      toast.error('Failed to skip step');
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    await initializeOnboarding();
  };

  const getStepStatus = (stepIndex) => {
    const step = onboardingSteps[stepIndex];
    
    if (completedSteps.has(step.id)) {
      return 'completed';
    } else if (stepIndex === currentStep) {
      return 'active';
    } else if (stepIndex < currentStep) {
      return 'pending';
    } else {
      return 'upcoming';
    }
  };

  const getStepIcon = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const step = onboardingSteps[stepIndex];
    
    switch (status) {
      case 'completed':
        return <CheckIcon color="success" />;
      case 'active':
        return step.icon;
      case 'pending':
        return <WarningIcon color="warning" />;
      default:
        return step.icon;
    }
  };

  const getProgressPercentage = () => {
    return (completedSteps.size / onboardingSteps.length) * 100;
  };

  const getEstimatedTimeRemaining = () => {
    const remainingSteps = onboardingSteps.slice(currentStep);
    return remainingSteps.reduce((total, step) => {
      const time = parseInt(step.estimatedTime);
      return total + (isNaN(time) ? 0 : time);
    }, 0);
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={4}>
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading onboarding...
        </Typography>
        <LinearProgress sx={{ width: '100%', mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box display="flex" gap={2}>
          <StyledButton
            variant="contained"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
          >
            Retry
          </StyledButton>
          <StyledButton
            variant="outlined"
            onClick={() => navigate('/patient-dashboard')}
            startIcon={<HomeIcon />}
          >
            Go to Dashboard
          </StyledButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <StyledCard>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box mb={4}>
            <Typography variant="h4" gutterBottom>
              Complete Your Profile
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Let's get you set up with MindWell. This will only take a few minutes.
            </Typography>
            
            {/* Progress */}
            <Box mb={3}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(getProgressPercentage())}% Complete
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={getProgressPercentage()}
                color="primary"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            {/* Time estimate */}
            <Box display="flex" gap={2} mb={3}>
              <Chip
                label={`${getEstimatedTimeRemaining()} minutes remaining`}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Step ${currentStep + 1} of ${onboardingSteps.length}`}
                color="secondary"
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={currentStep} orientation="vertical">
            {onboardingSteps.map((step, index) => (
              <Step key={step.id} completed={completedSteps.has(step.id)}>
                <StepLabel
                  icon={getStepIcon(index)}
                  optional={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {step.estimatedTime}
                      </Typography>
                      {!step.required && (
                        <Chip
                          label="Optional"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  }
                >
                  <Typography variant="h6">
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box>
                    {/* Step content will be rendered here */}
                    <Typography variant="body1">
                      Step content for {step.title} will be rendered here.
                    </Typography>
                    
                    {/* Navigation buttons */}
                    <Box display="flex" gap={2} mt={3}>
                      {index > 0 && (
                        <StyledButton
                          variant="outlined"
                          onClick={handleStepBack}
                          startIcon={<BackIcon />}
                        >
                          Back
                        </StyledButton>
                      )}
                      
                      <StyledButton
                        variant="contained"
                        onClick={() => handleStepComplete(step.id, stepData[step.id] || {})}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : <NextIcon />}
                      >
                        {saving ? 'Saving...' : 'Complete & Continue'}
                      </StyledButton>
                      
                      {!step.required && (
                        <StyledButton
                          variant="text"
                          onClick={() => handleSkipStep(step.id)}
                          disabled={saving}
                        >
                          Skip for Now
                        </StyledButton>
                      )}
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* Validation errors */}
          {Object.keys(validationErrors).length > 0 && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <Typography variant="body2">
                Please fix the following errors:
              </Typography>
              <ul>
                {Object.entries(validationErrors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Session status */}
          <Box mt={4}>
            <Divider />
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                Session Status: {getSessionStats().status}
              </Typography>
              <Tooltip title="Refresh onboarding data">
                <IconButton onClick={handleRefresh} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default UnifiedOnboardingFlow;